<template>
  <div class="home survey-home mobile-responsive-share">
    <v-wait for="questionnaireListLoading">
      <template slot="waiting">
        <HalfCircleSpinner />
        <div class="spinner-message my-3 text-center">
          Loading the questionnaires...
        </div>
      </template>
      <questionnaires-list
        type="current"
        title-text="Current Studies"
        :questionnaires="currentQuestionnaires"
        empty-list-message="There are no current studies"
        @reseted-study="getQuestionnairesList"
        @fill-user-profile="showUserProfilePopup"
      />
      <div class="mt-4">
        <short-questionnaires-list
          title-text="Upcoming Studies"
          :questionnaires="upcomingQuestionnaires"
          :empty-list-message="`There are no studies starting within the next ${upcomingLimit} days.`"
        />
      </div>
      <div class="mt-4">
        <short-questionnaires-list
          title-text="Closed Studies"
          :questionnaires="previousQuestionnaires"
          empty-list-message="There are no closed studies"
        />
      </div>
    </v-wait>
    <b-modal
      size="xl"
      :visible="isUserProfilePopupVisible"
      class="v3_modal"
      :header-class="'align-items-center border_0'"
      :body-class="'p-0'"
      hide-footer
      @hidden="isUserProfilePopupVisible = false"
    >
      <template slot="modal-header">
        <h2 class="font_size_1125rem">User Profile</h2>
        <div class="d-flex align-items-center justify-content-between">
          <b-button variant="v3_teal_outline" @click="onUserProfileSave">
            <template v-if="!userProfilePopupLoading">Save</template>
            <b-spinner v-else small />
          </b-button>
          <b-button
            variant="v3_red_outline"
            class="ml-3"
            @click="isUserProfilePopupVisible = false"
          >
            Cancel
          </b-button>
        </div>
      </template>
      <user-profile-form
        ref="userProfileModal"
        @profile-form-submit="onProfileSubmit"
        @profile-form-cancel="isUserProfilePopupVisible = false"
        @validate-failed="userProfilePopupLoading = false"
      />
    </b-modal>
  </div>
</template>

<script>
import Api from '@/providers/Api.js';
import QuestionnairesList from '@/components/survey/home/QuestionnairesList';
import { HalfCircleSpinner } from 'epic-spinners';
import { handleServerErrorWithValidation } from '@/helpers/ToastedHelper';
import UserProfileForm from '@/components/users/profile/UserProfileForm';
import ShortQuestionnairesList from "@/components/survey/home/ShortQuestionnairesList";

const sortQuestionnaire = function (a, b) {
  let numA = 0;
  let numB = 0;
  // first if is Primary
  numA += a.isPrimary ? 1000 : 0;
  numB += b.isPrimary ? 1000 : 0;

  // next if survey is started but not submitted
  numA += a.survey && !a.survey.submitted ? 100 : 0;
  numB += b.survey && !b.survey.submitted ? 100 : 0;

  // next if survey is not started
  numA += !a.survey ? 10 : 0;
  numB += !b.survey ? 10 : 0;

  // next if survey is submitted
  numA += a.survey && a.survey.submitted ? 1 : 0;
  numB += b.survey && b.survey.submitted ? 1 : 0;

  // last sort by completion_date
  return numB - numA || a.completion_date > b.completion_date;
};

export default {
  name: 'SurveyHome',
  components: {
    QuestionnairesList,
    HalfCircleSpinner,
    ShortQuestionnairesList,
    UserProfileForm,
  },
  emitedQuestionnaireId: null,
  data: function () {
    return {
      questionnaires: [],
      resetLoading: false,
      isUserProfilePopupVisible: false,
      userProfilePopupLoading: false,
    };
  },
  created: function () {
    this.getQuestionnairesList();
  },
  computed: {
    currentQuestionnaires: function () {
      return this.questionnaires
        .filter((q) => q.isActive)
        .sort(sortQuestionnaire);
    },
    previousQuestionnaires: function () {
      return this.questionnaires.filter((q) => q.isClosed);
    },
    upcomingLimit: function () {
      return this.$store.state.settings.upcoming_days_limit || 30;
    },
    upcomingQuestionnaires: function () {
      return this.questionnaires.filter(
        ({ start_date, isActive, isClosed }) => {
          return (
            !isActive &&
            !isClosed &&
            this.$dayjs(start_date).isAfter(this.$dayjs()) &&
            this.$dayjs(start_date).isSameOrBefore(
              this.$dayjs().add(this.upcomingLimit, 'day')
            )
          );
        }
      );
    },
    society: function () {
      return this.user && this.user.society_id ? this.user.society : {};
    },
    societyName: function () {
      return this.society ? this.society.name : '';
    },
    isDemoMode: function () {
      return this.$store.getters.isDemoMode;
    },
    user: function () {
      return this.$store.state.user;
    },
  },
  methods: {
    getQuestionnairesList: async function () {
      this.$wait.start('questionnaireListLoading');
      try {
        this.questionnaires = await Api.getQuestionnairesForSurveyHome();
      } catch (error) {
        handleServerErrorWithValidation(error, this.$toasted);
      } finally {
        this.$wait.end('questionnaireListLoading');
      }
    },
    showUserProfilePopup: async function (questionnaireId) {
      this.$options.emitedQuestionnaireId = questionnaireId;
      this.isUserProfilePopupVisible = true;
    },
    onUserProfileSave: function () {
      this.userProfilePopupLoading = true;
      this.$refs.userProfileModal &&
      typeof this.$refs.userProfileModal.save === 'function' &&
      this.$refs.userProfileModal.save();
    },
    onProfileSubmit: function () {
      this.userProfilePopupLoading = false;
      this.isUserProfilePopupVisible = false;
      if (this.$options.emitedQuestionnaireId) {
        this.$root.$emit(`user-profile-form-submitted-${this.$options.emitedQuestionnaireId}`);
        this.emitedQuestionnaireId = null;
      }
    }
  },
};
</script>

<style scoped>
@media (min-width: 320px) and (max-width: 768px) {
  .mobile-responsive-share{
    padding: 0px 15px;
  }
}
</style>

<style lang="scss" scoped>
label {
  font-weight: bold;
}
.data-row {
  height: 100%;
  min-height: 5rem;
}
</style>
